import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { soundNotificationService, SoundType } from '@/services/soundNotificationService';

// Sound options for the alarm
export const ALARM_SOUNDS = [
  { id: 'classic', name: 'Classic Beep', frequencies: [880, 660, 880] },
  { id: 'gentle', name: 'Gentle Chime', frequencies: [523, 659, 784] },
  { id: 'urgent', name: 'Urgent Alert', frequencies: [1000, 800, 1000, 800] },
  { id: 'bell', name: 'Bell', frequencies: [400, 500, 600] },
  { id: 'digital', name: 'Digital', frequencies: [1200, 1400, 1200] },
] as const;

export type AlarmSoundId = typeof ALARM_SOUNDS[number]['id'];

interface TimerState {
  // Countdown timer
  minutes: number;
  seconds: number;
  totalSeconds: number;
  originalTime: number;
  isRunning: boolean;
  isExpired: boolean;
  timerSound: AlarmSoundId;
  
  // Scheduled alarm
  scheduledTime: string | null; // HH:mm format
  isAlarmSet: boolean;
  isAlarmTriggered: boolean;
  alarmSound: AlarmSoundId;
  
  // Settings
  isSettingsOpen: boolean;
}

interface TimerContextType {
  timer: TimerState;
  isFlashing: boolean;
  isAlarmPlaying: boolean;
  inputMinutes: number;
  setInputMinutes: (minutes: number) => void;
  startTimer: () => void;
  pauseTimer: () => void;
  stopTimer: () => void;
  setCustomTime: (minutes: number) => void;
  handleCompleteTask: () => void;
  toggleSettings: () => void;
  formatTimeDisplay: (totalSeconds: number) => string;
  // Scheduled alarm functions
  setScheduledAlarm: (time: string, sound?: AlarmSoundId) => void;
  cancelScheduledAlarm: () => void;
  dismissAlarm: () => void;
  // Sound functions
  setTimerSound: (soundId: AlarmSoundId) => void;
  setAlarmSound: (soundId: AlarmSoundId) => void;
  previewSound: (soundId: AlarmSoundId) => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

const TIMER_STORAGE_KEY = 'taskTimer';

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getInitialState = (): TimerState => {
    try {
      const saved = localStorage.getItem(TIMER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Check scheduled alarm
        if (parsed.isAlarmSet && parsed.scheduledTime) {
          const [hours, mins] = parsed.scheduledTime.split(':').map(Number);
          const now = new Date();
          const alarmTime = new Date();
          alarmTime.setHours(hours, mins, 0, 0);
          
          if (alarmTime <= now) {
            // Alarm time has passed
            return {
              ...parsed,
              isAlarmTriggered: true,
            };
          }
        }
        
        // Check countdown timer
        if (parsed.isRunning && parsed.startedAt) {
          const elapsed = Math.floor((Date.now() - parsed.startedAt) / 1000);
          const remaining = Math.max(0, parsed.totalSeconds - elapsed);
          
          if (remaining <= 0) {
            return {
              ...parsed,
              totalSeconds: 0,
              minutes: 0,
              seconds: 0,
              isRunning: false,
              isExpired: true,
            };
          }
          
          return {
            ...parsed,
            totalSeconds: remaining,
            minutes: Math.floor(remaining / 60),
            seconds: remaining % 60,
          };
        }
        return parsed;
      }
    } catch (e) {
      console.error('Failed to load timer state:', e);
    }
    
    return {
      minutes: 25,
      seconds: 0,
      totalSeconds: 25 * 60,
      originalTime: 25 * 60,
      isRunning: false,
      isExpired: false,
      timerSound: 'classic',
      scheduledTime: null,
      isAlarmSet: false,
      isAlarmTriggered: false,
      alarmSound: 'urgent',
      isSettingsOpen: false,
    };
  };

  const [timer, setTimer] = useState<TimerState>(getInitialState);
  const [inputMinutes, setInputMinutes] = useState(25);
  const [isFlashing, setIsFlashing] = useState(false);
  const [isAlarmPlaying, setIsAlarmPlaying] = useState(false);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const alarmIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const scheduledAlarmRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const saveTimerState = useCallback((state: TimerState, startedAt?: number) => {
    try {
      localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify({
        ...state,
        startedAt: state.isRunning ? (startedAt || Date.now()) : undefined,
      }));
    } catch (e) {
      console.error('Failed to save timer state:', e);
    }
  }, []);

  // Play alarm with selected sound
  const playAlarmBeep = useCallback((soundId: AlarmSoundId) => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const sound = ALARM_SOUNDS.find(s => s.id === soundId) || ALARM_SOUNDS[0];
      const now = audioContext.currentTime;
      
      sound.frequencies.forEach((freq, i) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(audioContext.destination);
        osc.frequency.value = freq;
        osc.type = sound.id === 'digital' ? 'square' : 'sine';
        
        const startTime = now + (i * 0.15);
        gain.gain.setValueAtTime(0.3, startTime);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.14);
        osc.start(startTime);
        osc.stop(startTime + 0.15);
      });
    } catch (error) {
      console.log('Could not play alarm beep:', error);
    }
  }, []);

  const activeSoundRef = useRef<AlarmSoundId>('classic');
  
  const startAlarm = useCallback((soundId: AlarmSoundId) => {
    if (alarmIntervalRef.current) return;
    
    activeSoundRef.current = soundId;
    setIsAlarmPlaying(true);
    playAlarmBeep(soundId);
    
    alarmIntervalRef.current = setInterval(() => {
      playAlarmBeep(activeSoundRef.current);
    }, 1500);
  }, [playAlarmBeep]);

  const stopAlarm = useCallback(() => {
    setIsAlarmPlaying(false);
    if (alarmIntervalRef.current) {
      clearInterval(alarmIntervalRef.current);
      alarmIntervalRef.current = null;
    }
  }, []);

  // Initialize audio context
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    if (timer.isExpired) {
      startAlarm(timer.timerSound);
    } else if (timer.isAlarmTriggered) {
      startAlarm(timer.alarmSound);
    }

    return () => {
      stopAlarm();
      if (scheduledAlarmRef.current) {
        clearTimeout(scheduledAlarmRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Check scheduled alarm every second
  useEffect(() => {
    if (!timer.isAlarmSet || !timer.scheduledTime || timer.isAlarmTriggered) return;

    const checkAlarm = setInterval(() => {
      const [hours, mins] = timer.scheduledTime!.split(':').map(Number);
      const now = new Date();
      
      if (now.getHours() === hours && now.getMinutes() === mins) {
        setTimer(prev => {
          const newState = { ...prev, isAlarmTriggered: true };
          saveTimerState(newState);
          return newState;
        });
        setIsFlashing(true);
        startAlarm(timer.alarmSound);
        
        toast.error(`Alarm! It's ${timer.scheduledTime}`, {
          duration: 10000,
          style: { background: '#dc2626', color: 'white' }
        });
        
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Alarm!', {
            body: `It's ${timer.scheduledTime}`,
            icon: '/icon-192.png'
          });
        }
      }
    }, 1000);

    return () => clearInterval(checkAlarm);
  }, [timer.isAlarmSet, timer.scheduledTime, timer.isAlarmTriggered, startAlarm, saveTimerState]);

  // Countdown timer effect
  useEffect(() => {
    if (timer.isRunning && !timer.isExpired) {
      intervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev.totalSeconds <= 0) {
            clearInterval(intervalRef.current!);
            setIsFlashing(true);
            toast.error('Time is up!', {
              duration: 5000,
              style: { background: '#dc2626', color: 'white' }
            });
            
            startAlarm(prev.timerSound);
            
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Timer Complete!', {
                body: 'Your timer has finished.',
                icon: '/icon-192.png'
              });
            }
            
            const newState = {
              ...prev,
              minutes: 0,
              seconds: 0,
              isRunning: false,
              isExpired: true,
            };
            saveTimerState(newState);
            return newState;
          }
          
          const newTotalSeconds = prev.totalSeconds - 1;
          return {
            ...prev,
            totalSeconds: newTotalSeconds,
            minutes: Math.floor(newTotalSeconds / 60),
            seconds: newTotalSeconds % 60,
          };
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timer.isRunning, timer.isExpired, saveTimerState, startAlarm]);

  // Flashing effect
  useEffect(() => {
    let flashInterval: NodeJS.Timeout | null = null;
    if (timer.isExpired || timer.isAlarmTriggered) {
      flashInterval = setInterval(() => {
        setIsFlashing(prev => !prev);
      }, 500);
    } else {
      setIsFlashing(false);
    }
    return () => {
      if (flashInterval) clearInterval(flashInterval);
    };
  }, [timer.isExpired, timer.isAlarmTriggered]);

  const startTimer = useCallback(() => {
    if (timer.totalSeconds > 0) {
      const newState = { ...timer, isRunning: true, isExpired: false };
      setTimer(newState);
      saveTimerState(newState, Date.now());
    }
  }, [timer, saveTimerState]);

  const pauseTimer = useCallback(() => {
    const newState = { ...timer, isRunning: false };
    setTimer(newState);
    saveTimerState(newState);
  }, [timer, saveTimerState]);

  const stopTimer = useCallback(() => {
    stopAlarm();
    const newState = {
      ...timer,
      minutes: Math.floor(timer.originalTime / 60),
      seconds: timer.originalTime % 60,
      totalSeconds: timer.originalTime,
      isRunning: false,
      isExpired: false,
    };
    setTimer(newState);
    setIsFlashing(false);
    saveTimerState(newState);
  }, [timer, saveTimerState, stopAlarm]);

  const setCustomTime = useCallback((minutes: number) => {
    const totalSeconds = minutes * 60;
    const newState = {
      ...timer,
      minutes,
      seconds: 0,
      totalSeconds,
      originalTime: totalSeconds,
      isRunning: false,
      isExpired: false,
      isSettingsOpen: false,
    };
    setTimer(newState);
    setInputMinutes(minutes);
    saveTimerState(newState);
  }, [timer, saveTimerState]);

  const handleCompleteTask = useCallback(() => {
    stopAlarm();
    soundNotificationService.playSound(SoundType.Success);
    stopTimer();
  }, [stopAlarm, stopTimer]);

  const toggleSettings = useCallback(() => {
    setTimer(prev => ({ ...prev, isSettingsOpen: !prev.isSettingsOpen }));
  }, []);

  const formatTimeDisplay = useCallback((totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Scheduled alarm functions
  const setScheduledAlarm = useCallback((time: string) => {
    const newState = {
      ...timer,
      scheduledTime: time,
      isAlarmSet: true,
      isAlarmTriggered: false,
      isSettingsOpen: false,
    };
    setTimer(newState);
    saveTimerState(newState);
    toast.success(`Alarm set for ${time}`);
  }, [timer, saveTimerState]);

  const cancelScheduledAlarm = useCallback(() => {
    stopAlarm();
    const newState = {
      ...timer,
      scheduledTime: null,
      isAlarmSet: false,
      isAlarmTriggered: false,
    };
    setTimer(newState);
    setIsFlashing(false);
    saveTimerState(newState);
  }, [timer, saveTimerState, stopAlarm]);

  const dismissAlarm = useCallback(() => {
    stopAlarm();
    const newState = {
      ...timer,
      isExpired: false,
      isAlarmTriggered: false,
      isAlarmSet: false,
      scheduledTime: null,
    };
    setTimer(newState);
    setIsFlashing(false);
    saveTimerState(newState);
  }, [timer, saveTimerState, stopAlarm]);

  // Sound functions
  const setTimerSound = useCallback((soundId: AlarmSoundId) => {
    const newState = { ...timer, timerSound: soundId };
    setTimer(newState);
    saveTimerState(newState);
  }, [timer, saveTimerState]);

  const setAlarmSound = useCallback((soundId: AlarmSoundId) => {
    const newState = { ...timer, alarmSound: soundId };
    setTimer(newState);
    saveTimerState(newState);
  }, [timer, saveTimerState]);

  const previewSound = useCallback((soundId: AlarmSoundId) => {
    playAlarmBeep(soundId);
  }, [playAlarmBeep]);

  return (
    <TimerContext.Provider value={{
      timer,
      isFlashing,
      isAlarmPlaying,
      inputMinutes,
      setInputMinutes,
      startTimer,
      pauseTimer,
      stopTimer,
      setCustomTime,
      handleCompleteTask,
      toggleSettings,
      formatTimeDisplay,
      setScheduledAlarm,
      cancelScheduledAlarm,
      dismissAlarm,
      setTimerSound,
      setAlarmSound,
      previewSound,
    }}>
      {children}
    </TimerContext.Provider>
  );
};

const NOOP = () => {};
const NOOP_FORMAT = (_s: number) => '00:00';

const FALLBACK_CONTEXT: TimerContextType = {
  timer: {
    minutes: 0, seconds: 0, totalSeconds: 0, originalTime: 0,
    isRunning: false, isExpired: false, timerSound: 'classic',
    scheduledTime: null, isAlarmSet: false, isAlarmTriggered: false,
    alarmSound: 'classic', isSettingsOpen: false,
  },
  isFlashing: false,
  isAlarmPlaying: false,
  inputMinutes: 25,
  setInputMinutes: NOOP,
  startTimer: NOOP,
  pauseTimer: NOOP,
  stopTimer: NOOP,
  setCustomTime: NOOP,
  handleCompleteTask: NOOP,
  toggleSettings: NOOP,
  formatTimeDisplay: NOOP_FORMAT,
  setScheduledAlarm: NOOP,
  cancelScheduledAlarm: NOOP,
  dismissAlarm: NOOP,
  setTimerSound: NOOP,
  setAlarmSound: NOOP,
  previewSound: NOOP,
};

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (context === undefined) {
    console.warn('useTimer called outside TimerProvider — returning fallback');
    return FALLBACK_CONTEXT;
  }
  return context;
};
